/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosEnumEdsGradients,
	CosmosModButtonFragment,
	EdsIconsEnum,
	IconSectionHeadingFragment,
	SectionHeadingFragment,
} from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosButtonProps,
	CosmosTextVariantEnum,
	CosmosVariableRichText,
	EdsIcon,
	TitleDescStack,
} from "@components/elements";
import { CosmosStackProps } from "@components/types";
import { useSectionTitleAs } from "@components/context";
import { css, cva, cx } from "@panda/css";
import { Box, HStack, VStack } from "@panda/jsx";

export enum SectingHeadingSizesEnum {
	md = "656px",
	lg = "1175px",
}
export type SectingHeadingSizes = keyof typeof SectingHeadingSizesEnum;

export interface SectionHeadingProps extends Omit<
	SectionHeadingFragment | IconSectionHeadingFragment,
	"buttons" | "modButtons" | "title" | "description" | "__typename"
> {
	__typename?: "CosmosElementSectionHeading" | "CosmosElementIconSectionHeading";
	icon?: EdsIconsEnum | null;
	iconGradient?: CosmosEnumEdsGradients | null;
	title?: CosmosVariableRichText | null;
	desc?: string | null;
	buttons?: CosmosButtonProps[];
	modButtons?: CosmosModButtonFragment[];
	size?: SectingHeadingSizes;
	titleDescStack?: CosmosStackProps;
	mobileTextAlignStart?: boolean;
	buttonAlignLeft?: boolean | null;
	className?: string;
}

const styled = cva({
	base: {
		width: "full",
		lgDown: {
			flexDirection: "column",
			gap: "eds.dimension.32",
		},

		smDown: {
			gap: "eds.dimension.24",
			"& .cosmos-text.cosmos-desc": {
				textStyle: "paragraphMD",
			},
		},
	},
	variants: {
		__typename: {
			CosmosElementIconSectionHeading: {
				"&&": {
					gap: "eds.dimension.24",
					"& .section-heading, & .title-desc-stack": {
						gap: "eds.dimension.16",
					},
					"& .button-list": {
						paddingBlock: "0",
					},
				},
			},
			CosmosElementSectionHeading: {},
			undefined: {},
		},
		// Some components want 1 step smaller textStyle for headings
		compressedTitle: {
			true: {
				lgDown: {
					flexDirection: "row",
				},
				smDown: {
					"&.cosmos-text.cosmos-title": {
						textStyle: "headingLG",
					},
				},
			},
			false: {
				mdDown: {
					"& .cosmos-text.cosmos-title": {
						textStyle: "heading2XL",
					},
				},
				smDown: {
					"& .cosmos-text.cosmos-title": {
						textStyle: "headingXL",
					},
				},
			},
		},
	},
});

export const SectionHeading = (props: SectionHeadingProps | undefined): React.JSX.Element | null => {
	// A surrounding provider (getSections' first hero-less section) can ask this title to be
	// the page h1. Called before the early return to satisfy the Rules of Hooks.
	const sectionTitleAs = useSectionTitleAs();
	if (!props) return null;

	const {
		__typename,
		icon,
		iconGradient,
		title,
		desc,
		compressedTitle,
		centeredVerticalStack,
		buttons = [],
		modButtons = [],
		size = "md",
		titleGradient,
		titleDescStack,
		mobileTextAlignStart = false,
		buttonAlignLeft = false,
		className,
	} = props;

	const validDesc = Boolean(desc);

	const buttonSource = modButtons?.length ? modButtons : buttons;

	if (!title?.text && !title?.html && !validDesc && !buttonSource.length) return null;

	const stackTitle: CosmosVariableRichText = {
		...title,
		variant: compressedTitle ? CosmosTextVariantEnum.headingLG : CosmosTextVariantEnum.heading2XL,
		as: title?.as || sectionTitleAs || "h2",
	};
	const stackDesc: CosmosVariableRichText = {
		html: desc,
		variant: CosmosTextVariantEnum.paragraphMD,
	};

	const cosmosButtons = buttonSource.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			variant: button.variant || "outline", //force default to outline
		};
	});

	const maxWidth = SectingHeadingSizesEnum[size] || SectingHeadingSizesEnum.md;

	let alignItems = "end";
	if (centeredVerticalStack) {
		alignItems = "center";
	} else if (buttonAlignLeft) {
		alignItems = "start";
	}
	const textAlign = centeredVerticalStack ? "center" : "start";
	const lgDownAlignItems = mobileTextAlignStart || (!centeredVerticalStack && validDesc) ? "start" : "center";
	const lgDownTextAlign =
		centeredVerticalStack || (!compressedTitle && !validDesc && !mobileTextAlignStart) ? "center" : "start";

	return (
		<HStack
			className={cx(
				`section-heading`,
				cx(styled({ __typename, compressedTitle: !!compressedTitle })),
				css({
					textAlign,
					lgDown: { alignItems: lgDownAlignItems, textAlign: lgDownTextAlign },
					xsDown: { flexDirection: "column" },
				}),
				className,
			)}
			alignItems={alignItems}
			justifyContent={centeredVerticalStack ? "center" : "space-between"}
			flexDirection={centeredVerticalStack || buttonAlignLeft ? "column" : "row"}
		>
			<VStack alignItems={centeredVerticalStack ? "center" : "flex-start"} gap="eds.dimension.24">
				{icon ? (
					<Box
						className={css({
							"& svg": {
								height: "eds.dimension.80!important", //override EDS style
								width: "eds.dimension.80!important",
							},
							lgDown: { alignSelf: lgDownAlignItems },
						})}
					>
						<EdsIcon size="xl" icon={icon} gradient={iconGradient} />
					</Box>
				) : null}
				<TitleDescStack
					title={stackTitle}
					description={stackDesc}
					stack={{ ...titleDescStack, gap: titleDescStack?.gap || "eds.dimension.24" }}
					gradient={titleGradient}
					className={css({ maxWidth, lgDown: { maxWidth: "full" } })}
				/>
			</VStack>
			{cosmosButtons?.length ? (
				<ButtonList
					className={css({
						h: "full",
						lg: {
							paddingBlock: buttonAlignLeft || centeredVerticalStack ? "eds.dimension.12" : "0",
						},
					})}
					buttons={cosmosButtons}
				/>
			) : null}
		</HStack>
	);
};
